import { CompositeLayer, Layer, PickingInfo } from "@deck.gl/core"
import GeometryPointsLayer from "./GeometryPointsLayer.class"
import GeometryLinesLayer, { PolygonGeometry } from "./GeometryLinesLayer.class"
import GeometryMidpointsLayer from "./GeometryMidpointLayer.class"
import store from "integrations/redux/store"
import { customMapController } from "features/map/control/slice"
import { PathLayerProps, PolygonLayerProps, ScatterplotLayerProps } from "@deck.gl/layers"
import { FillStyleExtensionProps, TerrainExtensionProps } from "@deck.gl/extensions"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import { setActiveSidePanelModal } from "panels/content/side-panel/slice"
import { LngLatCoordinatePair } from "features/map/event/types"
import { geometryDataStore, GeometryDataStore } from "./GeometryDataStore"
import { isEqual } from "nodash"
// deckgl dependency, I don't want to control the version
// oxlint-disable-next-line nova/no-implicit-dependencies
import { MjolnirGestureEvent } from "mjolnir.js"
import { firePerimeterDragController } from "../useMapDrawingOverlay"
import { setActiveFeatureId, setActiveLayerId } from "panels/content/side-panel/slice.new"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { Geometry } from "./types"

export enum GeometryMode {
    Polygon = 1,
    LineToPolygon = 2,
    Line = 3,
    MultiPolygon = 4,
}

export type Midpoint = {
    midpoint: LngLatCoordinatePair
    first: LngLatCoordinatePair
    second: LngLatCoordinatePair
}

export type GeometryLayerStyles<T> = {
    points: Partial<ScatterplotLayerProps<T>>
    selectedPoint: Partial<ScatterplotLayerProps<T>>
    midpoints: Partial<ScatterplotLayerProps<T>>
    lines: Partial<PathLayerProps<T>>
    polygons: Partial<PolygonLayerProps<T> & FillStyleExtensionProps & TerrainExtensionProps> & {
        getStroke?: () => number
    }
}

export type ExtraLayer =
    | { provider: "points"; layer: (data: LngLatCoordinatePair[]) => Layer }
    | {
          provider: "midpoints"
          layer: (data: Midpoint[]) => Layer
      }
    | {
          provider: "polygons"
          layer: (
              prefix: string,
              data: PolygonGeometry[],
              thinLinePoint?: LngLatCoordinatePair
          ) => Layer
      }

export type EditData = {
    canEdit: true
    flush: { current: (store: GeometryDataStore, save: boolean) => void }
    onPolyComplete?: { current: () => void }
    onReset?: { current: () => void }
}

type DrawData = {
    canDraw: boolean
    drawMode: "smooth" | "points"
    maxPoints?: number
}

export default class GeometryLayer<T extends Geometry = Geometry> extends CompositeLayer<{
    id: string
    data: PolygonGeometry[]
    pickable?: boolean
    editData: EditData | undefined
    drawData: DrawData

    mode: GeometryMode // fire perimeter uses Polygon, Ruler/Lines uses LineToPolygon

    styles: GeometryLayerStyles<T>
    extraLayers: ExtraLayer[]
    geometryName: string

    selectedId: string
    initEdit: (id: string, idx: number) => void
    onHover: (info: PickingInfo, event: MjolnirGestureEvent) => boolean | void
    canCloseLine?: boolean
}> {
    state = {} as {
        points: LngLatCoordinatePair[]
        midPoints: Midpoint[]
        polygons: PolygonGeometry[]
        cleanup?: () => void
        id: string

        current: number
        placingPoint?: LngLatCoordinatePair
        clickDownWillPanMap?: boolean
        clickDownWillPanMapTimeout?: NodeJS.Timeout

        updateRef: Date
        drawData: DrawData
    }
    layerName = this.props.id + "layer_name"
    updateState({ changeFlags }: { changeFlags: Record<string, unknown> }) {
        geometryDataStore.layer = this as unknown as GeometryLayer
        const data = this.props.data
        if (changeFlags.dataChanged) {
            if (this.props.editData) {
                if (data?.length && !geometryDataStore.isAcquired()) {
                    const acquired = geometryDataStore.acquire(
                        this.props.id,
                        data[0],
                        this.props.editData,
                        this.props.mode
                    )
                    if (acquired) {
                        this.setState({ id: this.props.id })
                        if (
                            this.props.drawData?.canDraw &&
                            geometryDataStore.isLine() &&
                            this.props.drawData.drawMode === "smooth"
                        ) {
                            geometryDataStore.acquireDrawing()
                        }
                    }
                }
            } else {
                this.setState({
                    polygons: data,
                })
            }
        }
        if (this.props.editData && !isEqual(this.props.drawData, this.state.drawData)) {
            if (this.state.cleanup) this.state.cleanup()
            if (this.props.drawData) this.initializeDrawListeners()
        }
        this.setState({ drawData: this.props.drawData })
    }

    initializeDrawListeners = () => {
        if (
            this.props.drawData &&
            this.props.drawData.canDraw &&
            geometryDataStore.isLine() &&
            this.props.drawData.drawMode === "smooth"
        ) {
            geometryDataStore.acquireDrawing()
        } else if (this.props.drawData.canDraw) {
            store.dispatch(
                customMapController({ options: { dragRotate: false, doubleClickZoom: false } })
            )
        }
        const map = document.getElementById("map") as HTMLElement | undefined

        const onMouseDown = (event: MouseEvent) => {
            if (
                event.button === 0 &&
                !this.state.placingPoint &&
                geometryDataStore.isLine() &&
                this.props.editData &&
                this.props.drawData.canDraw &&
                this.props.drawData.drawMode === "points" &&
                (event.target as HTMLElement).className === "maplibregl-canvas"
            ) {
                const { longitude, latitude } = resolveEventLngLat(event)
                const lngLat: LngLatCoordinatePair = [longitude, latitude]

                this.setState({
                    placingPoint: lngLat,
                    // Resets placingPoint, the point that is defined here and will be
                    // used to create a real point on mouseUp, IF AND ONLY IF onMouseMove
                    // happens within the next 100ms. This is to allow map panning without
                    // making a point on every single mouseUp event.
                    clickDownWillPanMap: true,
                    clickDownWillPanMapTimeout: setTimeout(() => {
                        this.setState({
                            clickDownWillPanMap: false,
                        })
                    }, 100),
                })
            } else {
                this.setState({ placingPoint: undefined })
            }
        }
        const preventDefault = (event: MouseEvent) => {
            event.preventDefault()
        }

        const onMouseUp = (event: MouseEvent & { overPoint?: boolean; isPolygon?: boolean }) => {
            if (event.button !== 0 || !this.state.placingPoint) return

            clearTimeout(this.state.clickDownWillPanMapTimeout)

            setTimeout(() => {
                const placingPoint =
                    this.state.placingPoint &&
                    ([...this.state.placingPoint] as LngLatCoordinatePair)

                this.setState({ placingPoint: undefined })

                if (placingPoint && !event.overPoint) {
                    const max = this.props.drawData?.maxPoints
                    if (!max || geometryDataStore.points.length < max) {
                        geometryDataStore.addPoint(placingPoint)
                    }
                }
            }, 0)
        }

        const onMouseMove = (event: MouseEvent) => {
            if (this.state.placingPoint && !this.state.clickDownWillPanMap) {
                this.setState({ placingPoint: undefined })
            }
            if (event.button) {
                geometryDataStore.updateThinLinePoint()
                return
            }
            if (
                geometryDataStore.isLine() &&
                (geometryDataStore.points.length || 0) > 0 &&
                (event.target as HTMLElement)?.className === "maplibregl-canvas" &&
                !event.buttons
            ) {
                const { longitude, latitude } = resolveEventLngLat(event)
                geometryDataStore.updateThinLinePoint([longitude, latitude] as [number, number])
            }
        }

        const onKeyDown = (event: KeyboardEvent) => {
            switch (event.key.toLowerCase()) {
                case "backspace":
                    if (event.ctrlKey || event.metaKey) {
                        this.props.editData?.onReset?.current()
                    } else {
                        geometryDataStore.deletePoint()
                    }
                    break
                case "enter":
                case "escape":
                    if (event.key === "Enter" && !event.shiftKey) {
                        if (
                            this.props.editData?.onPolyComplete &&
                            geometryDataStore.points.length >= 3
                        ) {
                            geometryDataStore.finalizePolygon()
                            this.props.editData.onPolyComplete.current()
                        } else {
                            geometryDataStore.free(true)
                        }
                    } else {
                        store.dispatch(setActiveTool(undefined))
                        geometryDataStore.free(false)
                    }
                    store.dispatch(setActiveSidePanelModal(undefined))
                    store.dispatch(setActiveLayerId({ id: undefined }))
                    store.dispatch(setActiveFeatureId({ feature: undefined }))

                    break
                case "z":
                    if (event.shiftKey && (event.ctrlKey || event.metaKey)) {
                        geometryDataStore.redo()
                    } else if (event.ctrlKey || event.metaKey) {
                        geometryDataStore.undo()
                    }
                    break
                case "y":
                    if (event.ctrlKey || event.metaKey) geometryDataStore.redo()
                    break
            }
        }

        map?.addEventListener("pointerdown", onMouseDown)
        map?.addEventListener("mousemove", onMouseMove)
        map?.addEventListener("pointerup", onMouseUp)
        window.addEventListener("contextmenu", preventDefault)
        window.addEventListener("keydown", onKeyDown)
        this.setState({
            cleanup: () => {
                map?.removeEventListener("pointerdown", onMouseDown)
                map?.removeEventListener("mousemove", onMouseMove)
                map?.removeEventListener("pointerup", onMouseUp)
                window.removeEventListener("contextmenu", preventDefault)
                window.removeEventListener("keydown", onKeyDown)
                store.dispatch(
                    customMapController({ options: { dragRotate: true, doubleClickZoom: true } })
                )
            },
        })
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- i cant figure out proper types for this
    onClick(...args: any[]) {
        args[0].sourceLayer?.onClick?.(...args)
        return true
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDragStart = (...args: any[]) => {
        firePerimeterDragController.emit("isDragging", true)
        args[0].sourceLayer?.onDragStart?.(...args)
        return true
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDrag = (...args: any[]) => {
        args[0].sourceLayer?.onDrag?.(...args)
        return true
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDragEnd = (...args: any[]) => {
        firePerimeterDragController.emit("isDragging", false)
        args[0].sourceLayer?.onDragEnd?.(...args)
        return true
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onHover = (...args: any[]) => {
        this.props.onHover?.(args[0], args[1])
        args[0].sourceLayer?.onHover?.(...args)
        return true
    }

    finalizeState() {
        this.state.cleanup?.()
    }

    renderLayers() {
        return [
            new GeometryLinesLayer({
                id: this.props.id + "lines",
                data: this.state.polygons || [geometryDataStore.polygon],
                visible: true,
                current: this.props.editData ? geometryDataStore.polygon.idx : -1,
                lineStyles: this.props.styles.lines,
                polygonStyles: this.props.styles.polygons,
                thinLinePoint: geometryDataStore.thinLinePoint,
                pickable:
                    this.props.pickable &&
                    !geometryDataStore.drawing.acquired &&
                    !this.props.editData,
                onLayerClick: (geometry: PolygonGeometry) => {
                    this.props.initEdit(geometry.id || "", geometry.idx)
                },
            }),
            ...(this.props.editData
                ? [
                      new GeometryMidpointsLayer({
                          id: this.props.id + "midpoints",
                          data: geometryDataStore.midpoints,
                          visible: true,
                          addPoint: geometryDataStore.addPoint.bind(geometryDataStore),
                          midpointStyles: this.props.styles.midpoints,
                      }),
                      new GeometryPointsLayer({
                          id: this.props.id + "points",
                          data: geometryDataStore.points,
                          pickable: true,
                          pointStyles: this.props.styles.points,
                          selectedPointStyles: this.props.styles.selectedPoint,
                          isLine: geometryDataStore.isLine(),
                          closePoly: () => {
                              if (this.props.editData?.onPolyComplete) {
                                  geometryDataStore.finalizePolygon()
                                  this.props.editData.onPolyComplete.current()
                              } else {
                                  geometryDataStore.closePoly()
                              }
                              geometryDataStore.emit("closed")
                          },
                          closeLine: () => {
                              if (this.props.editData?.onPolyComplete) {
                                  geometryDataStore.finalizePolygon()
                                  this.props.editData.onPolyComplete.current()
                              } else {
                                  geometryDataStore.free(true)
                              }
                              geometryDataStore.emit("closed")
                          },
                          canCloseLine: this.props.canCloseLine ?? true,
                          geometryName: this.props.geometryName,
                      }),
                  ]
                : []),
            ...this.props.extraLayers?.map((e) => {
                if (e.provider === "points") return e.layer(geometryDataStore.points)
                if (e.provider === "midpoints") return e.layer(geometryDataStore.midpoints)
                if (e.provider === "polygons")
                    return e.layer(
                        this.props.id,
                        this.state.polygons || [geometryDataStore.polygon],
                        geometryDataStore.thinLinePoint
                    )
            }),
        ]
    }
}
